import {
  Badge,
  Box,
  Center,
  Flex,
  HStack,
  Spinner,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import {
  useGetInvitationByTokenQuery,
  useAcceptInvitationMutation,
  useDeclineInvitationMutation,
} from '../../generated/graphql';
import { useAuth } from '../../contexts/AuthContext';

const getRoleBadgeColor = (role: string) => {
  switch (role) {
    case 'OWNER':
      return 'purple';
    case 'ADMIN':
      return 'blue';
    default:
      return 'gray';
  }
};

export const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isAuthenticated, backendUser, refreshBackendUser, loading: authLoading } = useAuth();
  const token = searchParams.get('token');

  const { data, loading, error } = useGetInvitationByTokenQuery({
    variables: { token: token || '' },
    skip: !token,
  });

  const [acceptInvitation, { loading: accepting }] = useAcceptInvitationMutation();
  const [declineInvitation, { loading: declining }] = useDeclineInvitationMutation();
  const [actionComplete, setActionComplete] = useState(false);

  const invitation = data?.getInvitationByToken;

  // Store pending invite token for unauthenticated users (so post-login redirects work)
  useEffect(() => {
    if (!authLoading && !isAuthenticated && token) {
      sessionStorage.setItem('pendingInviteToken', token);
      if (invitation?.email) {
        sessionStorage.setItem('pendingInvitationEmail', invitation.email);
      }
    }
  }, [authLoading, isAuthenticated, token, invitation?.email]);

  // Check if there's a pending invite after login
  useEffect(() => {
    if (isAuthenticated && !token) {
      const pendingToken = sessionStorage.getItem('pendingInviteToken');
      if (pendingToken) {
        sessionStorage.removeItem('pendingInviteToken');
        navigate(`/accept-invite?token=${pendingToken}`);
      }
    }
  }, [isAuthenticated, token, navigate]);

  const handleSignIn = () => {
    if (token) {
      sessionStorage.setItem('pendingInviteToken', token);
      if (invitation?.email) {
        sessionStorage.setItem('pendingInvitationEmail', invitation.email);
      }
    }
    navigate('/login', { state: { from: { pathname: `/accept-invite?token=${token}` } } });
  };

  const handleAccept = async () => {
    if (!token) return;

    try {
      const result = await acceptInvitation({
        variables: { token },
      });

      await refreshBackendUser();
      setActionComplete(true);

      toast({
        title: 'Welcome!',
        description: `You have joined ${result.data?.acceptInvitation.name}`,
        status: 'success',
        duration: 3000,
      });

      // Redirect to settings after a brief delay
      setTimeout(() => {
        navigate('/settings');
      }, 1500);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to accept invitation',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleDecline = async () => {
    if (!token) return;

    try {
      await declineInvitation({
        variables: { token },
      });

      setActionComplete(true);

      toast({
        title: 'Invitation declined',
        status: 'info',
        duration: 2000,
      });

      setTimeout(() => {
        navigate('/home');
      }, 1500);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to decline invitation',
        status: 'error',
        duration: 5000,
      });
    }
  };

  if (authLoading || loading) {
    return (
      <Center minH="400px">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (!token) {
    return (
      <Center minH="400px">
        <VStack spacing={4}>
          <HelivanText variant="sectionHeader">Invalid Link</HelivanText>
          <HelivanText variant="smallBody">
            This invitation link is invalid or missing.
          </HelivanText>
          <HelivanButton variant="primary" onClick={() => navigate('/home')}>
            Go to Home
          </HelivanButton>
        </VStack>
      </Center>
    );
  }

  if (error || !invitation) {
    return (
      <Center minH="400px">
        <VStack spacing={4}>
          <HelivanText variant="sectionHeader">Invitation Not Found</HelivanText>
          <HelivanText variant="smallBody">
            This invitation may have expired, been revoked, or already been used.
          </HelivanText>
          <HelivanButton variant="primary" onClick={() => navigate('/login')}>
            Go to Login
          </HelivanButton>
        </VStack>
      </Center>
    );
  }

  // Check if invitation is for the current user's email
  const emailMismatch =
    isAuthenticated &&
    backendUser?.email &&
    backendUser.email.toLowerCase() !== invitation.email.toLowerCase();

  // Check if invitation is expired
  const isExpired = new Date(invitation.expiresAt) < new Date();

  // Check if already processed
  const isProcessed = invitation.status !== 'PENDING';

  if (actionComplete) {
    return (
      <Center minH="400px">
        <VStack spacing={4}>
          <Spinner />
          <HelivanText variant="smallBody">Redirecting...</HelivanText>
        </VStack>
      </Center>
    );
  }

  return (
    <Center minH="400px" px={4}>
      <Box
        maxW="500px"
        w="full"
        p={8}
        borderRadius="lg"
        border="1px solid"
        borderColor={HelivanColor.border.primary}
        bg={HelivanColor.background.primary}
      >
        <VStack spacing={6} align="start">
          <HelivanText variant="sectionHeader">Organization Invitation</HelivanText>

          {isProcessed ? (
            <VStack spacing={4} align="start" width="full">
              <HelivanText variant="smallBody">
                This invitation has already been {invitation.status.toLowerCase()}.
              </HelivanText>
              <HelivanButton variant="primary" onClick={() => navigate('/home')}>
                Go to Home
              </HelivanButton>
            </VStack>
          ) : isExpired ? (
            <VStack spacing={4} align="start" width="full">
              <HelivanText variant="smallBody">
                This invitation has expired. Please ask the organization admin to send a new invitation.
              </HelivanText>
              <HelivanButton variant="primary" onClick={() => navigate('/home')}>
                Go to Home
              </HelivanButton>
            </VStack>
          ) : !isAuthenticated ? (
            // Show invitation details with sign-in prompt for unauthenticated users
            <>
              <VStack spacing={3} align="start" width="full">
                <HelivanText variant="smallBody">
                  You've been invited to join:
                </HelivanText>
                <HelivanText variant="largeSubheader" colorOverride={HelivanColor.text.blueDark}>
                  {invitation.organization?.name}
                </HelivanText>
              </VStack>

              <HStack spacing={4}>
                <VStack align="start" spacing={1}>
                  <HelivanText variant="tinyBody+">Invited by</HelivanText>
                  <HelivanText variant="smallBody">
                    {invitation.invitedByUser?.fullName}
                  </HelivanText>
                </VStack>
                <VStack align="start" spacing={1}>
                  <HelivanText variant="tinyBody+">Role</HelivanText>
                  <Badge colorScheme={getRoleBadgeColor(invitation.role)}>
                    {invitation.role}
                  </Badge>
                </VStack>
              </HStack>

              <Box
                p={4}
                bg={HelivanColor.background.secondary}
                borderRadius="md"
                width="full"
              >
                <HelivanText variant="smallBody">
                  Sign in or create an account to accept this invitation.
                </HelivanText>
              </Box>

              <Flex width="full" justify="center" pt={2}>
                <HelivanButton
                  variant="primary"
                  onClick={handleSignIn}
                >
                  Sign in to continue
                </HelivanButton>
              </Flex>
            </>
          ) : emailMismatch ? (
            <VStack spacing={4} align="start" width="full">
              <HelivanText variant="smallBody" colorOverride="red.500">
                This invitation was sent to {invitation.email}, but you are signed in as {backendUser?.email}.
              </HelivanText>
              <HelivanText variant="smallBody">
                Please sign in with the correct account to accept this invitation.
              </HelivanText>
              <HelivanButton variant="primary" onClick={() => navigate('/login')}>
                Sign In
              </HelivanButton>
            </VStack>
          ) : (
            <>
              <VStack spacing={3} align="start" width="full">
                <HelivanText variant="smallBody">
                  You've been invited to join:
                </HelivanText>
                <HelivanText variant="largeSubheader" colorOverride={HelivanColor.text.blueDark}>
                  {invitation.organization?.name}
                </HelivanText>
              </VStack>

              <HStack spacing={4}>
                <VStack align="start" spacing={1}>
                  <HelivanText variant="tinyBody+">Invited by</HelivanText>
                  <HelivanText variant="smallBody">
                    {invitation.invitedByUser?.fullName}
                  </HelivanText>
                </VStack>
                <VStack align="start" spacing={1}>
                  <HelivanText variant="tinyBody+">Role</HelivanText>
                  <Badge colorScheme={getRoleBadgeColor(invitation.role)}>
                    {invitation.role}
                  </Badge>
                </VStack>
              </HStack>

              <Flex width="full" justify="space-between" pt={4}>
                <HelivanButton
                  variant="ghost"
                  onClick={handleDecline}
                  isLoading={declining}
                  isDisabled={accepting || declining}
                >
                  Decline
                </HelivanButton>
                <HelivanButton
                  variant="primary"
                  onClick={handleAccept}
                  isLoading={accepting}
                  isDisabled={accepting || declining}
                >
                  Accept Invitation
                </HelivanButton>
              </Flex>
            </>
          )}
        </VStack>
      </Box>
    </Center>
  );
};
